import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  Alert,
  Button,
  CircularProgress,
  IconButton,
  Popover,
  Snackbar,
  Tab,
  Tabs,
  Tooltip,
  Typography,
} from '@mui/material';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import {
  AlertTriangle,
  ArrowLeft,
  Check,
  CheckCircle2,
  Copy,
  Plug,
  ShieldAlert,
} from 'lucide-react';
import { useTheme } from '../../context/ThemeContext';
import mcpServerService from '../../services/mcpServerService';
import { copyTextToClipboard } from '../../utils/clipboard';
import PageLoader from '../shared/PageLoader';
import sharedStyles from '../PlatformAdmin/PlatformAdmin.module.scss';
import styles from './McpServerDetail.module.scss';
import LintPanel from './LintPanel';
import McpPublishDialog from './McpPublishDialog';
import ImportSpecTab from './tabs/ImportSpecTab';
import CurateToolsTab from './tabs/CurateToolsTab';
import ToolTestPanel from './tabs/ToolTestPanel';
import CallLogsTab from './tabs/CallLogsTab';
import ConnectTab from './tabs/ConnectTab';
import SettingsTab from './tabs/SettingsTab';
import {
  CREDENTIAL_MODES,
  EMPTY_LINT,
  countEnabled,
  describeError,
  getCredentialMode,
  getServerStatusMeta,
  getServerTypeMeta,
  isNotFound,
  isPerUser,
  isPublished,
  normalizeLint,
  serverNeedsImport,
  unwrapList,
} from './mcpUtils';

const LINT_DEBOUNCE_MS = 800;

/**
 * MCP server detail — the shell that owns `server`, `tools`, `lint` and
 * `tokens`, and hands them to whichever tab is showing.
 *
 * Layout is a tabbed page with a progress strip rather than a locked wizard:
 * the strip guides a first run (import → curate → publish → connect) while a
 * customer coming back to an existing server can jump straight to Logs or
 * Settings.
 */
const McpServerDetail = () => {
  const { serverId } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { isDarkMode } = useTheme();

  const [server, setServer] = useState(null);
  const [tools, setTools] = useState([]);
  const [tokens, setTokens] = useState([]);
  const [install, setInstall] = useState(null);
  const [lint, setLint] = useState(EMPTY_LINT);
  const [lintLoading, setLintLoading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [error, setError] = useState(null);
  const [publishOpen, setPublishOpen] = useState(false);
  const [unpublishing, setUnpublishing] = useState(false);
  const [copiedUrl, setCopiedUrl] = useState(false);
  const [lintAnchor, setLintAnchor] = useState(null);
  const [toast, setToast] = useState({ open: false, message: '', severity: 'info' });
  const [testToolId, setTestToolId] = useState(null);
  // Set by a lint link: the Tools tab scrolls to this tool and opens its editor.
  const [focusToolId, setFocusToolId] = useState(null);
  // Set by a lint "re-import (sync)" link: preselects the mode on the Import tab.
  const [importPresetMode, setImportPresetMode] = useState(null);

  const lintTimerRef = useRef(null);
  const didAutoSelectTab = useRef(false);

  const muiTheme = useMemo(
    () =>
      createTheme({
        palette: {
          mode: isDarkMode ? 'dark' : 'light',
          primary: { main: '#e36250' },
          secondary: { main: '#8b5cf6' },
        },
      }),
    [isDarkMode]
  );

  const notify = useCallback((message, severity = 'info') => {
    setToast({ open: true, message, severity });
  }, []);

  // ── Data ────────────────────────────────────────────────────────────────

  const loadServer = useCallback(async () => {
    try {
      const data = await mcpServerService.getServer(serverId);
      setServer(data);
      setNotFound(false);
      return data;
    } catch (e) {
      // 404 is "not found OR not yours" — deliberately indistinguishable, so
      // never render anything implying the server exists.
      if (isNotFound(e)) setNotFound(true);
      else setError(describeError(e, "Couldn't load this MCP server."));
      return null;
    }
  }, [serverId]);

  const loadTools = useCallback(async () => {
    try {
      const data = await mcpServerService.listTools(serverId);
      const list = unwrapList(data, 'tools');
      setTools(list);
      return list;
    } catch (e) {
      if (!isNotFound(e)) setError(describeError(e, "Couldn't load the tool list."));
      return [];
    }
  }, [serverId]);

  const loadTokens = useCallback(async () => {
    try {
      const data = await mcpServerService.listTokens(serverId);
      setTokens(unwrapList(data, 'tokens'));
    } catch (e) {
      // Token metadata is supporting information — a failure here should not
      // take down the whole screen.
      setTokens([]);
    }
  }, [serverId]);

  /**
   * The server object doesn't carry `public_url` — only publish and install
   * return it — so a published server needs this call before the header can
   * show a URL. Fetched once here and handed to the Connect tab so it isn't
   * requested twice.
   */
  const loadInstall = useCallback(async () => {
    try {
      const data = await mcpServerService.getInstall(serverId);
      setInstall(data);
      return data;
    } catch (e) {
      setInstall(null);
      return null;
    }
  }, [serverId]);

  const runLint = useCallback(async () => {
    setLintLoading(true);
    try {
      const data = await mcpServerService.lint(serverId);
      setLint(normalizeLint(data));
    } catch (e) {
      if (!isNotFound(e)) setLint(EMPTY_LINT);
    } finally {
      setLintLoading(false);
    }
  }, [serverId]);

  /** Re-lint after edits settle, so the header reflects what the customer just did. */
  const scheduleLint = useCallback(() => {
    if (lintTimerRef.current) clearTimeout(lintTimerRef.current);
    lintTimerRef.current = setTimeout(() => runLint(), LINT_DEBOUNCE_MS);
  }, [runLint]);

  useEffect(() => () => lintTimerRef.current && clearTimeout(lintTimerRef.current), []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setNotFound(false);
    setError(null);
    didAutoSelectTab.current = false;

    (async () => {
      const s = await loadServer();
      if (cancelled) return;
      if (s) {
        const jobs = [loadTools(), loadTokens(), runLint()];
        if (isPublished(s)) jobs.push(loadInstall());
        await Promise.all(jobs);
      }
      if (!cancelled) setLoading(false);
    })();

    return () => {
      cancelled = true;
    };
  }, [loadServer, loadTools, loadTokens, runLint, loadInstall]);

  // ── Tabs ────────────────────────────────────────────────────────────────

  const needsImport = server ? serverNeedsImport(server) : false;
  const published = isPublished(server);
  const enabledCount = countEnabled(tools);
  // `public_url` isn't on the server object — install is the reliable source.
  const publicUrl = server?.public_url || install?.url || null;

  const tabs = useMemo(() => {
    const list = [];
    if (needsImport) list.push({ value: 'import', label: 'Import' });
    list.push({ value: 'tools', label: 'Tools' });
    list.push({ value: 'test', label: 'Test' });
    if (published) list.push({ value: 'connect', label: 'Connect' });
    list.push({ value: 'logs', label: 'Logs' });
    list.push({ value: 'settings', label: 'Settings' });
    return list;
  }, [needsImport, published]);

  const requestedTab = searchParams.get('tab');
  const activeTab = tabs.some((t) => t.value === requestedTab) ? requestedTab : tabs[0]?.value;

  const goToTab = useCallback(
    (value) => {
      const next = new URLSearchParams(searchParams);
      next.set('tab', value);
      setSearchParams(next, { replace: true });
    },
    [searchParams, setSearchParams]
  );

  // First arrival with no ?tab=: an api server with nothing imported starts at
  // Import; everything else starts on the tool list.
  useEffect(() => {
    if (loading || !server || didAutoSelectTab.current) return;
    didAutoSelectTab.current = true;
    if (requestedTab) return;
    goToTab(needsImport && tools.length === 0 ? 'import' : 'tools');
  }, [loading, server, requestedTab, needsImport, tools.length, goToTab]);

  // ── Mutations ───────────────────────────────────────────────────────────

  /** Every tool/settings edit funnels through here so lint stays in step. */
  const handleMutated = useCallback(
    async ({ reloadServer = false, reloadTools = true, reloadTokens = false } = {}) => {
      const jobs = [];
      if (reloadServer) jobs.push(loadServer());
      if (reloadTools) jobs.push(loadTools());
      if (reloadTokens) jobs.push(loadTokens());
      await Promise.all(jobs);
      scheduleLint();
    },
    [loadServer, loadTools, loadTokens, scheduleLint]
  );

  const handlePublished = async (result) => {
    setPublishOpen(false);
    // Publish returns public_url + the install block directly — use it rather
    // than making the Connect tab fetch the same thing again.
    if (result?.install || result?.public_url) {
      setInstall(result.install || { ...(install || {}), url: result.public_url });
    } else {
      await loadInstall();
    }
    await loadServer();
    await runLint();
    notify('Published. Your connector is live.', 'success');
    goToTab('connect');
    return result;
  };

  const handleUnpublish = async () => {
    setUnpublishing(true);
    try {
      await mcpServerService.unpublish(serverId);
      setInstall(null);
      await loadServer();
      await runLint();
      notify('Unpublished — the public URL now returns 404.', 'info');
      goToTab('tools');
    } catch (e) {
      notify(describeError(e, "Couldn't unpublish."), 'error');
    } finally {
      setUnpublishing(false);
    }
  };

  const handleCopyUrl = async () => {
    try {
      await copyTextToClipboard(publicUrl);
      setCopiedUrl(true);
      setTimeout(() => setCopiedUrl(false), 2000);
    } catch (e) {
      notify('Copy failed — select the URL and copy it manually.', 'warning');
    }
  };

  const handleOpenTest = (toolId) => {
    setTestToolId(toolId);
    goToTab('test');
  };

  // ── Lint fixes (invalid tool schemas) ──────────────────────────────────

  const handleOpenTool = (toolId) => {
    setLintAnchor(null);
    setFocusToolId(toolId);
    goToTab('tools');
  };

  /**
   * Lints straight away rather than through the debounced `handleMutated`:
   * this is usually clicked from the publish dialog, where the fresh result is
   * what un-blocks the Publish button.
   */
  const handleDisableTool = async (toolId) => {
    try {
      await mcpServerService.updateTool(serverId, toolId, { enabled: false });
      await Promise.all([loadTools(), runLint()]);
      notify('Tool disabled.', 'success');
    } catch (e) {
      notify(describeError(e, "Couldn't disable the tool."), 'error');
    }
  };

  const handleReimportSync = () => {
    setLintAnchor(null);
    setImportPresetMode('sync');
    goToTab('import');
  };

  const clearFocusTool = useCallback(() => setFocusToolId(null), []);
  const clearImportPreset = useCallback(() => setImportPresetMode(null), []);

  // Only `api` servers have a spec to re-import.
  const lintFixProps = {
    onOpenTool: handleOpenTool,
    onDisableTool: handleDisableTool,
    onReimportSync: needsImport ? handleReimportSync : undefined,
  };

  // ── Render ──────────────────────────────────────────────────────────────

  if (loading) return <PageLoader message="Loading MCP server..." />;

  if (notFound) {
    return (
      <ThemeProvider theme={muiTheme}>
        <div className={sharedStyles.layout}>
          <div className={sharedStyles.inner}>
            <div className={sharedStyles.emptyState}>
              <Plug size={44} className={sharedStyles.emptyIcon} />
              <Typography variant="h6">This MCP server doesn’t exist</Typography>
              <Typography variant="body2" className={sharedStyles.muted}>
                It may have been deleted, or the link is wrong.
              </Typography>
              <Button
                className={sharedStyles.outlineBtn}
                variant="outlined"
                onClick={() => navigate('/mcp-servers')}
                sx={{ mt: 2 }}
              >
                Back to MCP servers
              </Button>
            </div>
          </div>
        </div>
      </ThemeProvider>
    );
  }

  const statusMeta = getServerStatusMeta(server);
  const typeMeta = getServerTypeMeta(server?.server_type);
  const blockingCount = lint.blocking.length;
  const warningCount = lint.warnings.length;

  const steps = [
    ...(needsImport
      ? [{ key: 'import', label: 'Import', done: tools.length > 0, onClick: () => goToTab('import') }]
      : []),
    { key: 'tools', label: 'Curate', done: enabledCount > 0, onClick: () => goToTab('tools') },
    {
      key: 'publish',
      label: 'Publish',
      done: published,
      onClick: () => (published ? goToTab('connect') : setPublishOpen(true)),
    },
    {
      key: 'connect',
      label: 'Connect',
      // Only token auth has anything left to do here — anonymous and sign-in
      // servers are connectable the moment they are published.
      done: published && (server?.inbound_auth_mode !== 'token' || tokens.some((t) => !t.revoked_at)),
      onClick: () => (published ? goToTab('connect') : notify('Publish the server first.', 'info')),
    },
  ];

  // The strip's "current" step is the first unfinished one — it's guidance, not
  // a lock: every step stays clickable.
  const currentStepIndex = Math.max(
    0,
    steps.findIndex((s) => !s.done)
  );

  const renderLintChip = () => {
    let tone = styles.chipSuccess;
    let Icon = CheckCircle2;
    let label = published ? 'No issues' : 'Ready to publish';
    if (blockingCount > 0) {
      tone = styles.chipDanger;
      Icon = ShieldAlert;
      label = `${blockingCount} blocking`;
    } else if (warningCount > 0) {
      tone = styles.chipWarning;
      Icon = AlertTriangle;
      label = `${warningCount} ${warningCount === 1 ? 'warning' : 'warnings'}`;
    }
    return (
      <button
        type="button"
        className={`${styles.lintChip} ${tone}`}
        onClick={(e) => setLintAnchor(e.currentTarget)}
      >
        {lintLoading ? <CircularProgress size={13} color="inherit" /> : <Icon size={14} />}
        {label}
      </button>
    );
  };

  const renderTab = () => {
    switch (activeTab) {
      case 'import':
        return (
          <ImportSpecTab
            server={server}
            tools={tools}
            onMutated={handleMutated}
            onGoToTools={() => goToTab('tools')}
            notify={notify}
            presetMode={importPresetMode}
            onPresetApplied={clearImportPreset}
          />
        );
      case 'tools':
        return (
          <CurateToolsTab
            server={server}
            tools={tools}
            onMutated={handleMutated}
            onTestTool={handleOpenTest}
            notify={notify}
            invalidTools={lint.invalidTools}
            focusToolId={focusToolId}
            onFocusHandled={clearFocusTool}
          />
        );
      case 'test':
        return (
          <ToolTestPanel
            server={server}
            tools={tools}
            initialToolId={testToolId}
            notify={notify}
          />
        );
      case 'connect':
        return (
          <ConnectTab
            server={server}
            install={install}
            tokens={tokens}
            onTokensChanged={loadTokens}
            notify={notify}
          />
        );
      case 'logs':
        return <CallLogsTab server={server} notify={notify} />;
      case 'settings':
        return (
          <SettingsTab
            server={server}
            tokens={tokens}
            onMutated={handleMutated}
            onUnpublish={handleUnpublish}
            unpublishing={unpublishing}
            notify={notify}
          />
        );
      default:
        return null;
    }
  };

  return (
    <ThemeProvider theme={muiTheme}>
      <div className={sharedStyles.layout}>
        <div className={sharedStyles.inner}>
          {/* ── Header ── */}
          <div className={styles.header}>
            <Button
              className={sharedStyles.backBtn}
              startIcon={<ArrowLeft size={16} />}
              onClick={() => navigate('/mcp-servers')}
            >
              MCP Servers
            </Button>

            <div className={styles.headerMain}>
              <div className={styles.headerTitle}>
                <span className={styles.headerIcon}>
                  <Plug size={20} />
                </span>
                <div className={styles.headerText}>
                  <Typography variant="h5" className={styles.name}>
                    {server?.name || 'Untitled server'}
                  </Typography>
                  <div className={styles.headerMeta}>
                    <span className={styles.typeTag}>{typeMeta.short}</span>
                    {isPerUser(server) && (
                      <Tooltip title={CREDENTIAL_MODES[getCredentialMode(server)].helper}>
                        <span className={`${styles.typeTag} ${styles.perUserTag}`}>
                          {CREDENTIAL_MODES[getCredentialMode(server)].badge}
                        </span>
                      </Tooltip>
                    )}
                    <span className={`${styles.pill} ${styles[statusMeta.tone]}`}>
                      {statusMeta.label}
                    </span>
                    {server?.inbound_auth_mode === 'none' && (
                      <Tooltip title="Anyone with the URL can call this server.">
                        <span className={`${styles.typeTag} ${styles.warnTag}`}>no auth</span>
                      </Tooltip>
                    )}
                    {renderLintChip()}
                  </div>
                </div>
              </div>

              <div className={styles.headerActions}>
                {published ? (
                  <Button
                    className={sharedStyles.outlineBtn}
                    variant="outlined"
                    onClick={handleUnpublish}
                    disabled={unpublishing}
                    startIcon={unpublishing ? <CircularProgress size={15} /> : null}
                  >
                    {unpublishing ? 'Unpublishing…' : 'Unpublish'}
                  </Button>
                ) : (
                  <Tooltip
                    title={
                      blockingCount > 0
                        ? `${blockingCount} ${blockingCount === 1 ? 'issue blocks' : 'issues block'} publishing — click the badge above for details.`
                        : ''
                    }
                  >
                    <span>
                      <Button
                        className={sharedStyles.primaryBtn}
                        variant="contained"
                        onClick={() => setPublishOpen(true)}
                        disabled={blockingCount > 0}
                      >
                        Publish
                      </Button>
                    </span>
                  </Tooltip>
                )}
              </div>
            </div>

            {published && publicUrl && (
              <div className={styles.urlBar}>
                <code className={styles.publicUrl}>{publicUrl}</code>
                <IconButton size="small" className={sharedStyles.subtleBtn} onClick={handleCopyUrl}>
                  {copiedUrl ? <Check size={15} color="#10b981" /> : <Copy size={15} />}
                </IconButton>
              </div>
            )}
          </div>

          {/* ── Progress strip ── */}
          <div className={styles.steps}>
            {steps.map((step, index) => (
              <button
                key={step.key}
                type="button"
                className={`${styles.step} ${
                  index === currentStepIndex && !step.done ? styles.stepActive : ''
                } ${step.done ? styles.stepDone : ''}`}
                onClick={step.onClick}
              >
                <span className={styles.stepIndex}>
                  {step.done ? <Check size={12} strokeWidth={3} /> : index + 1}
                </span>
                {step.label}
              </button>
            ))}
          </div>

          {error && (
            <Alert severity="error" onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          <Tabs
            value={activeTab || false}
            onChange={(_, value) => goToTab(value)}
            className={sharedStyles.tabs}
            variant="scrollable"
            scrollButtons="auto"
          >
            {tabs.map((t) => (
              <Tab key={t.value} value={t.value} label={t.label} className={sharedStyles.tab} />
            ))}
          </Tabs>

          <div className={styles.tabBody}>{renderTab()}</div>
        </div>
      </div>

      <Popover
        open={!!lintAnchor}
        anchorEl={lintAnchor}
        onClose={() => setLintAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        slotProps={{ paper: { className: styles.lintPopover } }}
      >
        <LintPanel
          lint={lint}
          loading={lintLoading}
          dense
          published={published}
          {...lintFixProps}
        />
      </Popover>

      <McpPublishDialog
        open={publishOpen}
        server={server}
        lint={lint}
        lintLoading={lintLoading}
        onClose={() => setPublishOpen(false)}
        onPublished={handlePublished}
        onRelint={runLint}
        {...lintFixProps}
      />

      <Snackbar
        open={toast.open}
        autoHideDuration={5000}
        onClose={() => setToast((t) => ({ ...t, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setToast((t) => ({ ...t, open: false }))}
          severity={toast.severity}
          sx={{ width: '100%' }}
        >
          {toast.message}
        </Alert>
      </Snackbar>
    </ThemeProvider>
  );
};

export default McpServerDetail;
